import * as React from "react";
import { IconPhone } from "./icons";

// ---------------------------------------------------------------------------
// Spinner — CSS border spinner
// ---------------------------------------------------------------------------
export type SpinnerSize = "sm" | "md" | "lg";

export interface SpinnerProps {
  size?: SpinnerSize;
  onDark?: boolean;
  label?: string;
  className?: string;
}

export function Spinner({
  size = "md",
  onDark = false,
  label = "Loading",
  className = "",
}: SpinnerProps) {
  const cls = [
    "spin",
    size === "sm" && "spin-sm",
    size === "lg" && "spin-lg",
    onDark && "spin-on-dark",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return <span className={cls} role="status" aria-label={label} />;
}

// ---------------------------------------------------------------------------
// ProgressBar — indeterminate or determinate
// ---------------------------------------------------------------------------
export interface ProgressBarProps {
  value?: number;
  label?: string;
  caption?: React.ReactNode;
  className?: string;
}

export function ProgressBar({ value, label, caption, className = "" }: ProgressBarProps) {
  const determinate = value != null;

  return (
    <div className={className}>
      {determinate ? (
        <div
          className="bar-d"
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label ?? "Progress"}
        >
          <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
        </div>
      ) : (
        <div className="bar-i" role="progressbar" aria-label={label ?? "Loading"} />
      )}
      {caption && <div className="ld-cap">{caption}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkeletonRow — shimmer placeholder for call/check log rows
// ---------------------------------------------------------------------------
export interface SkeletonRowProps {
  titleWidth?: string;
  subtitleWidth?: string;
  className?: string;
}

export function SkeletonRow({
  titleWidth = "38%",
  subtitleWidth = "72%",
  className = "",
}: SkeletonRowProps) {
  return (
    <div className={["skel-row", className].filter(Boolean).join(" ")}>
      <div className="skel skel-dot" />
      <div className="skel-lines">
        <div className="skel skel-line" style={{ width: titleWidth }} />
        <div className="skel skel-line skel-line-sm" style={{ width: subtitleWidth }} />
      </div>
      <div className="skel skel-pill" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkeletonRows — multiple skeleton rows (common pattern)
// ---------------------------------------------------------------------------
export interface SkeletonRowsProps {
  count?: number;
  className?: string;
}

export function SkeletonRows({ count = 3, className = "" }: SkeletonRowsProps) {
  const widths = [
    { title: "38%", subtitle: "72%" },
    { title: "30%", subtitle: "58%" },
    { title: "42%", subtitle: "64%" },
    { title: "34%", subtitle: "68%" },
    { title: "36%", subtitle: "55%" },
  ];
  return (
    <div className={className} role="status" aria-label="Loading">
      {Array.from({ length: count }, (_, i) => {
        const w = widths[i % widths.length]!;
        return <SkeletonRow key={i} titleWidth={w.title} subtitleWidth={w.subtitle} />;
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CallPulse — branded ripple for active call state
// ---------------------------------------------------------------------------
export interface CallPulseProps {
  icon?: React.ReactNode;
  label?: string;
  className?: string;
}

export function CallPulse({
  icon = <IconPhone style={{ width: 18, height: 18 }} />,
  label = "Call in progress",
  className = "",
}: CallPulseProps) {
  return (
    <div className={["call-pulse", className].filter(Boolean).join(" ")} aria-label={label}>
      <span className="call-ring" />
      <span className="call-ring" />
      <span className="call-ring" />
      <span className="call-core">{icon}</span>
    </div>
  );
}
